// Вложения в поле ввода чата: что приложил владелец (скриншот из буфера, перетащенные файлы, скрепка), загрузка
// с прогрессом сразу после добавления — к отправке файлы обычно уже на месте. Окончательно тип и размер проверяет
// api по содержимому; здесь — быстрые проверки, чтобы не грузить заведомо лишнее.
import { ATTACH_LIMITS, type Attachment, type AttachmentKind } from '@autoaff/protocol';

export interface Pending {
  key: string;
  name: string;
  size: number;
  /** Вид по типу из браузера — до ответа api. */
  kind: AttachmentKind;
  /** Картинка для миниатюры до загрузки (object URL). */
  preview: string | null;
  status: 'uploading' | 'ready' | 'error';
  progress: number;
  error?: string;
  result?: Attachment;
}

const MB = 1024 * 1024;
export const SIZE_TEXT = {
  image: `Картинка больше ${ATTACH_LIMITS.imageBytes / MB} МБ — уменьшите её или сделайте скриншот`,
  file: 'Файл больше 1 ГБ',
};
export const TYPE_TEXT = 'Такой файл приложить нельзя';

/** Программы и архивы, которые агент не откроет, — отказ сразу, без загрузки (api проверит и сам). */
const BLOCKED = /\.(exe|msi|bat|cmd|com|scr|ps1|vbs|jar|apk|dmg|dll|iso|rar|7z)$/i;
const HEIC = /\.(heic|heif)$/i;

/** Вид файла по типу из браузера (картинка SVG — это файл: как картинку её не показываем). */
export function guessKind(f: { name: string; type: string }): AttachmentKind {
  if (f.type === 'image/svg+xml') return 'file';
  if (f.type.startsWith('image/') || HEIC.test(f.name)) return 'image';
  if (f.type.startsWith('video/')) return 'video';
  if (f.type.startsWith('audio/')) return 'audio';
  return 'file';
}

/** Сразу понятная ошибка: пустой, слишком большой, программа. null — можно грузить. */
export function precheck(f: { name: string; type: string; size: number }): string | null {
  if (f.size === 0) return 'Пустой файл';
  if (BLOCKED.test(f.name)) return TYPE_TEXT;
  if (guessKind(f) === 'image' && f.size > ATTACH_LIMITS.imageBytes) return SIZE_TEXT.image;
  if (f.size > ATTACH_LIMITS.fileBytes) return SIZE_TEXT.file;
  return null;
}

/** Скриншот из буфера у Chrome называется «image.png» — даём понятное имя со временем. */
export function pastedName(f: File, now = new Date()): string {
  if (f.name && !/^image\.(png|jpe?g|gif|webp)$/i.test(f.name)) return f.name;
  const ext = (f.type.split('/')[1] ?? 'png').replace('jpeg', 'jpg');
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `Скриншот ${hh}-${mm}.${ext}`;
}

/**
 * Фото HEIC с iPhone: если браузер умеет его открыть (Safari), отправляем JPEG — модель HEIC не видит.
 * Не умеет — уходит как есть: api сохранит файл, агент получит путь.
 */
export async function heicToJpeg(f: File): Promise<File> {
  if (!HEIC.test(f.name) && !/image\/hei[cf]/.test(f.type)) return f;
  try {
    const bmp = await createImageBitmap(f);
    const c = document.createElement('canvas');
    c.width = bmp.width;
    c.height = bmp.height;
    c.getContext('2d')?.drawImage(bmp, 0, 0);
    bmp.close();
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/jpeg', 0.92));
    if (!blob) return f;
    return new File([blob], f.name.replace(HEIC, '.jpg'), { type: 'image/jpeg' });
  } catch {
    return f;
  }
}

/** Длительность и кадр видео/звука от браузера — api возьмёт их, если сам из файла не узнает (webm и т. п.). */
export function mediaHints(f: File): Promise<Record<string, string>> {
  const kind = guessKind(f);
  if (kind !== 'video' && kind !== 'audio') return Promise.resolve({});
  // mp4, mov и m4a api разбирает сам
  if (/^(video\/(mp4|quicktime)|audio\/(mp4|x-m4a))$/.test(f.type)) return Promise.resolve({});
  return new Promise((resolve) => {
    const el = document.createElement(kind === 'video' ? 'video' : 'audio');
    const url = URL.createObjectURL(f);
    const done = (v: Record<string, string>) => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      el.removeAttribute('src');
      resolve(v);
    };
    const timer = setTimeout(() => done({}), 3000);
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      const v: Record<string, string> = {};
      if (Number.isFinite(el.duration) && el.duration > 0) v.duration = String(el.duration);
      if (el instanceof HTMLVideoElement && el.videoWidth) {
        v.width = String(el.videoWidth);
        v.height = String(el.videoHeight);
      }
      done(v);
    };
    el.onerror = () => done({});
    el.src = url;
  });
}

/** Загрузка потоком с прогрессом (у fetch нет прогресса отправки). */
export function uploadAttachment(
  file: File,
  hints: Record<string, string>,
  onProgress: (p: number) => void,
): { promise: Promise<Attachment>; abort: () => void } {
  const x = new XMLHttpRequest();
  const promise = new Promise<Attachment>((resolve, reject) => {
    x.open('POST', `/api/threads/attachments?${new URLSearchParams({ name: file.name, ...hints })}`);
    x.setRequestHeader('content-type', 'application/octet-stream');
    x.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    x.onload = () => {
      let data: { error?: string } | null = null;
      try {
        data = JSON.parse(x.responseText);
      } catch {
        data = null;
      }
      if (x.status === 401) window.dispatchEvent(new CustomEvent('aaff:unauthorized'));
      if (x.status >= 200 && x.status < 300) resolve(data as Attachment);
      else reject(new Error(data?.error ?? 'Не удалось загрузить файл'));
    };
    x.onerror = () => reject(new Error('Нет связи с платформой — файл не загрузился'));
    x.onabort = () => reject(new Error('Загрузка отменена'));
    x.send(file);
  });
  return { promise, abort: () => x.abort() };
}

/** Ссылка на вложение для отправки: api всё остальное возьмёт из самих файлов. */
export const refOf = (a: Attachment) => ({
  path: a.path,
  name: a.name,
  ...(a.view ? { view: a.view } : {}),
  ...(a.thumb ? { thumb: a.thumb } : {}),
  ...(a.durationSec ? { durationSec: a.durationSec } : {}),
  ...(a.width ? { width: a.width } : {}),
  ...(a.height ? { height: a.height } : {}),
});

export type AttachmentRef = ReturnType<typeof refOf>;

export const mediaSrc = (rel: string, download?: string) =>
  `/api/media/${rel}${download ? `?download=1&name=${encodeURIComponent(download)}` : ''}`;
